import React, { useEffect, useState } from 'react'
import DatePicker, { DatePickerProps } from 'react-datepicker'
import { createPortal } from 'react-dom'
import 'react-datepicker/dist/react-datepicker.css'

/**
 * Wrapper component for react-datepicker that fixes popper visibility in modals.
 * The calendar popper is rendered via Portal outside the modal DOM hierarchy,
 * preventing clipping issues. Uses fixed positioning to ensure visibility.
 *
 * @param props - All standard DatePicker props plus optional ariaLabel
 * @returns Configured DatePicker component
 * @notExported
 */
const CaleoDatePicker: React.FC<DatePickerProps & { ariaLabel?: string; withTime?: boolean }> = ({
  ariaLabel,
  customInput,
  placeholderText,
  id,
  ariaLabelledBy,
  showTimeSelect,
  withTime = false,
  popperContainer: customPopperContainer,
  ...rest
}) => {
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null)
  const resolvedAriaLabel = ariaLabel ?? (typeof placeholderText === 'string' ? placeholderText : undefined)

  // Create a portal container outside the modal on mount
  useEffect(() => {
    const div = document.createElement('div')
    div.setAttribute('data-datepicker-portal', 'true')
    document.body.appendChild(div)
    setPortalContainer(div)

    return () => {
      if (document.body.contains(div)) {
        document.body.removeChild(div)
      }
    }
  }, [])

  // Custom popper container that renders the calendar in a portal
  const CustomPopperContainer = (popperProps: Record<string, unknown>) => {
    const { children, style } = popperProps as { children: React.ReactNode; style: React.CSSProperties }

    if (!portalContainer) {
      return <div style={style}>{children}</div>
    }

    return createPortal(
      <div
        style={{
          ...style,
          position: 'fixed',
          zIndex: 9999,
        }}
      >
        {children}
      </div>,
      portalContainer
    )
  }

  const testId = id ? `datepicker-field-${id}` : undefined
  const resolvedCustomInput = React.isValidElement(customInput)
    ? React.cloneElement(customInput as React.ReactElement<Record<string, unknown>>, {
        ...((customInput as React.ReactElement<Record<string, unknown>>).props ?? {}),
        'aria-label':
          ((customInput as React.ReactElement<Record<string, unknown>>).props as { 'aria-label'?: string })?.[
            'aria-label'
          ] ?? resolvedAriaLabel,
      })
    : customInput

  return (
    <DatePicker
      {...rest}
      customInput={resolvedCustomInput}
      placeholderText={placeholderText}
      id={id}
      ariaLabelledBy={ariaLabelledBy}
      showTimeSelect={withTime || showTimeSelect}
      popperContainer={customPopperContainer || CustomPopperContainer}
      data-testid={testId}
    />
  )
}

export default CaleoDatePicker
